import { useState, useEffect, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays, Search, Mic, CheckCircle2, XCircle, FileText, Trash2, Clock,
} from "lucide-react";

const STATUS = {
  scheduled: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};

const fmtTime = (t) => {
  if (!t || !t.includes(":")) return t || "";
  const [H, M] = t.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(H)) return t;
  const ap = H >= 12 ? "PM" : "AM";
  let h = H % 12; if (h === 0) h = 12;
  return `${h}:${String(M || 0).padStart(2, "0")} ${ap}`;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AppointmentsList({ onOpenConsult }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [q, setQ] = useState("");
  const [allDates, setAllDates] = useState(false);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!allDates && date) params.date = date;
      if (q) params.q = q;
      const r = await api.get("/appointments", { params });
      setAppts(r.data);
    } catch { toast.error("Failed to load appointments"); }
    finally { setLoading(false); }
  }, [date, q, allDates]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setStatus = async (a, status) => {
    await api.post(`/appointments/${a.id}/status`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const remove = async (a) => {
    await api.delete(`/appointments/${a.id}`);
    toast.success("Appointment deleted");
    load();
  };

  const groups = appts.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort();

  return (
    <div className="space-y-5" data-testid="appointments-tab">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="appointments-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by patient name or unique ID…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
        </div>
        <input data-testid="appointments-date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setAllDates(false); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
        <div className="flex gap-2">
          <button data-testid="appointments-today-btn" onClick={() => { setDate(today); setAllDates(false); }} className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${!allDates && date === today ? "bg-[#1E5AA8] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Today</button>
          <button data-testid="appointments-all-btn" onClick={() => setAllDates((v) => !v)} className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${allDates ? "bg-[#1E5AA8] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>All Dates</button>
        </div>
      </div>

      {loading ? (
        <div className="h-32 flex items-center justify-center text-slate-400">Loading appointments…</div>
      ) : dates.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-12 text-center text-slate-400">
          <CalendarDays className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          {allDates ? "No appointments found." : `No appointments on ${date}.`}
        </div>
      ) : (
        dates.map((d) => (
          <div key={d} className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
            <div className={`px-5 py-3 flex items-center justify-between border-b ${d === today ? "bg-amber-50 border-amber-100" : "bg-[#EBF3FB] border-blue-100"}`}>
              <span className="font-semibold text-slate-800 inline-flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#1E5AA8]" /> {d}
                {d === today && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F59E0B] text-white font-bold">TODAY</span>}
              </span>
              <span className="text-xs text-slate-500">{groups[d].length} appointment{groups[d].length > 1 ? "s" : ""}</span>
            </div>
            <div className="divide-y divide-slate-50">
              {groups[d].map((a) => (
                <div key={a.id} data-testid={`appointment-row-${a.id}`} className="p-4 flex flex-wrap items-center gap-3 hover:bg-slate-50/60 transition-colors">
                  <div className="w-20 text-center">
                    <p className="font-mono-id font-bold text-[#123C73] text-sm whitespace-nowrap">{fmtTime(a.time)}</p>
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-medium text-slate-800" data-notranslate>{a.patient?.name || "Patient"} <span className="text-xs text-slate-400">{a.patient?.age}/{a.patient?.gender}</span></p>
                    <p className="text-xs font-mono-id text-[#1E5AA8]" data-notranslate>{a.patient_id}</p>
                    {a.notes && <p className="text-xs text-slate-400 mt-0.5">{a.notes}</p>}
                    {(a.booked_by || a.last_modified_by) && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400" data-testid={`appointment-audit-${a.id}`}>
                        {a.booked_by && (
                          <span>Booked by <span className="font-medium text-slate-500" data-notranslate>{a.booked_by.name}</span>
                            {a.booked_by.role === "assistant" && <span className="ml-1 px-1.5 py-px rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">assistant</span>}
                          </span>
                        )}
                        {a.last_modified_by && (
                          <span className="text-slate-300">·&nbsp;
                            <span className="text-slate-400">Last edited by <span className="font-medium text-slate-500" data-notranslate>{a.last_modified_by.name}</span>
                              {a.last_modified_by.role === "assistant" && <span className="ml-1 px-1.5 py-px rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">assistant</span>}
                              {a.last_modified_at && <span className="text-slate-400"> on {fmtWhen(a.last_modified_at)}</span>}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {a.reports?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {a.reports.map((rep) => (
                          <a key={rep.file_id} href={`${API}/appointments/${a.id}/reports/${rep.file_id}`} target="_blank" rel="noreferrer" data-testid="report-link"
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[#EBF3FB] text-[#1E5AA8] hover:bg-blue-100">
                            <FileText className="w-3 h-3" /> {rep.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full border capitalize ${STATUS[a.status] || STATUS.scheduled}`}>{a.status}</span>
                  <div className="flex gap-1">
                    {a.status === "scheduled" && (
                      <>
                        <button data-testid="appointment-consult-btn" onClick={() => onOpenConsult(a.patient_id)} title="Start consultation" className="p-2 rounded-lg hover:bg-[#EBF3FB] text-[#1E5AA8]"><Mic className="w-4 h-4" /></button>
                        <button data-testid="appointment-complete-btn" onClick={() => setStatus(a, "completed")} title="Mark completed" className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></button>
                        <button data-testid="appointment-cancel-btn" onClick={() => setStatus(a, "cancelled")} title="Cancel" className="p-2 rounded-lg hover:bg-red-50 text-red-500"><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    <button onClick={() => remove(a)} title="Delete" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
